import axios, { AxiosError, AxiosProgressEvent, AxiosRequestConfig, ResponseType } from "axios"
import { useNavigate } from "react-router-dom"
import { getPATtoken } from "src/lib/utils"

type mode = 'protected' | 'public'

type RestCommonProps = {
    url: string,
    params?: AxiosRequestConfig['params'],
    mode?: mode,
    responseType?: ResponseType,
    withCredentials?: boolean
}

type RestDataProps<T> = RestCommonProps & {
    data?: T
}

type postMethodProps = {
    onUploadProgress?: ((progressEvent: AxiosProgressEvent) => void)
}

type ResponseCommonKeys = {
    success: boolean,
    msg: string,
    errors?: any,
    validationErrors?: { key: string, message: string }[],
    statusCode: number
}

export type AxiosResponseCustom<T> = ResponseCommonKeys & T



export function useRestAPI() {


    const navigate = useNavigate()

    // GET request
    const get = async <T>({ url, params, mode = 'public', responseType, withCredentials = false }: RestCommonProps): Promise<AxiosResponseCustom<T>> => {
        try {

            let PAT = getPATtoken()

            const headers: any = {

            }


            const response = await axios.get<T>(url, {
                params: params,
                headers,
                responseType: responseType,
                withCredentials,
                auth: mode === 'protected' ?
                    {
                        username: '',
                        password: PAT!
                    } : undefined
            })

            if (response.status === 203) {
                navigate('/login', { replace: true })
                return {
                    success: false,
                    msg: 'Unauthorized',
                    statusCode: 401
                } as any
            }

            return {
                ...response.data,
                success: true,
                msg: '',
                statusCode: response.status
            }
        } catch (error: AxiosError | any) {
            if (axios.isAxiosError(error) && error.response?.status == 401 && mode === 'protected') {
                return handleError(error, mode)

            }
            
            
            return {
                success: false,
                msg: 'Network error',
                statusCode: 500
            } as any
        }
    }

    // POST request
    const post = async <T, D>({ url, data, params, mode = 'public', onUploadProgress, withCredentials = false }: RestDataProps<D> & postMethodProps): Promise<AxiosResponseCustom<T>> => {
        try {
            let PAT = getPATtoken()

            const headers: any = {

            }

            const response = await axios.post<AxiosResponseCustom<T>>(url, data, {
                params: params,
                headers,
                onUploadProgress,
                withCredentials,
                auth: mode === 'protected' ?
                    {
                        username: '',
                        password: PAT!
                    } : undefined
            })
            if (response.status === 203) {
                navigate('/login', { replace: true })
                return {
                    success: false,
                    msg: 'Unauthorized',
                    statusCode: 401
                } as any
            }
            return {
                ...response.data,
                success: true,
                msg: '',
                statusCode: response.status
            }
        } catch (error: AxiosError | any) {
            if (axios.isAxiosError(error) && error.response?.status == 401 && mode === 'protected') {
                return handleError(error, mode)

            }
            return {
                success: false,
                msg: 'Network error',
                statusCode: 500
            } as any
        }
    }

    // PUT request
    const put = async <T, D>({ url, data, params, mode = 'public', withCredentials = false }: RestDataProps<D>): Promise<AxiosResponseCustom<T>> => {
        try {
            const headers: any = {

            }
            if (mode === 'protected') {
                headers.Authorization = `Bearer ${localStorage.getItem('accessTocken')}`
            }
            const response = await axios.put<AxiosResponseCustom<T>>(url, data, {
                params: params,
                headers,
                withCredentials
            })
            return response.data
        } catch (error: AxiosError | any) {
            return handleError(error, mode)
        }
    }
    // PATCH request
    const patch = async <T, D>({ url, data, params, mode = 'public', withCredentials = false }: RestDataProps<D>): Promise<AxiosResponseCustom<T>> => {
        try {
            const headers: any = {

            }
            if (mode === 'protected') {
                headers.Authorization = `Bearer ${localStorage.getItem('accessTocken')}`
            }
            const response = await axios.patch<AxiosResponseCustom<T>>(url, data, {
                params: params,
                headers,
                withCredentials
            })
            return response.data
        } catch (error: AxiosError | any) {
            return handleError(error, mode)
        }
    }

    // DELETE request
    const del = async <T>({ url, params, mode = 'public', withCredentials = false }: RestCommonProps): Promise<AxiosResponseCustom<T>> => {
        try {
            const headers: any = {

            }
            if (mode === 'protected') {
                headers.Authorization = `Bearer ${localStorage.getItem('accessTocken')}`
            }
            const response = await axios.delete<AxiosResponseCustom<T>>(url, {
                params: params,
                headers,
                withCredentials
            })
            return response.data
        } catch (error: AxiosError | any) {
            return handleError(error, mode)
        }
    }

    // Error handling function
    const handleError = (error: AxiosError | any, mode: mode): AxiosResponseCustom<any> => {
        if (error.response?.status >= 400 && error.response?.status < 500 && mode === 'protected') {
            navigate('/login', { replace: true })
            return {
                success: false,
                msg: 'authentication error',
                statusCode: 401
            };
        }
        if (error.response?.data && error.response.data.success === false && error.response.data.msg) {
            return {
                success: false,
                msg: 'something went wrong',
                statusCode: error.response?.status
            }
        }
        if (error.code === 'ERR_NETWORK') {
            return {
                success: false,
                msg: 'Network Error',
                statusCode: 500
            }
        }
        return {
            success: false,
            msg: 'Server Error',
            statusCode: 500
        }
    }

    return {
        get,
        post,
        put,
        patch,
        delete: del
    }
}